import { ChevronLeft, ChevronRight } from 'lucide-react';
import { orgYear } from '@/lib/today';
import * as React from 'react';
import { formatDuration } from '@cfl/shared';

import {
  Badge, Button, Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { ExportMenu } from '@/components/export-menu';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { downloadCsv } from '@/lib/csv';
import { useInactiveProjects, useOvertime, useProjectYear, useTeamYear } from '@/lib/team-api';
import { downloadXlsx } from '@/lib/xlsx';
import { usePeriodShortcuts } from '@/lib/shortcuts';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** `2026-04` as `Apr`. The columns come from the API, in the order it bucketed them. */
const monthLabel = (month: string) => MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;

/**
 * A year as a grid, and the projects nobody is booking to.
 *
 * The previous system had four screens for the first shape - user-year,
 * weekly-users, monthly-users, yearly-users. They are one table with a different
 * period, so this is one screen.
 */
export function YearReportPage() {
  // Null means "whichever year is current", which only the API knows: the year
  // runs April to March, so from January to March getUTCFullYear() names a year
  // that has not started and the screen would open empty.
  const [year, setYear] = React.useState<number | null>(null);
  const [mode, setMode] = React.useState<'hours' | 'balance' | 'projects'>('hours');
  const { data, isPending } = useTeamYear(year);
  const { data: overtime } = useOvertime(year);
  // Only fetched when its tab is open: it is a second full-year scan and nobody
  // pays for it while looking at the people grid.
  const { data: projectYear } = useProjectYear(year, mode === 'projects');

  const shown = data?.year ?? overtime?.year ?? orgYear();
  const label = data?.label ?? overtime?.label ?? String(shown);
  // Headed from the same list the figures were bucketed into, so a column can
  // never be labelled with a month other than the one it holds.
  const months = data?.months ?? overtime?.months ?? [];
  const headers = months.length > 0 ? months.map(monthLabel) : MONTH_NAMES;

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setYear(shown - 1),
    next: () => setYear(shown + 1),
    today: () => setYear(null),
  });
  const [idleDays, setIdleDays] = React.useState(90);
  const { data: idle } = useInactiveProjects(idleDays);

  const columns = [
    { header: 'Person', width: 26 },
    ...headers.map((m) => ({ header: m, width: 10, numeric: true })),
    { header: 'Total', width: 12, numeric: true },
  ];
  const rows = () =>
    (data?.people ?? []).map((p) => [
      p.name,
      ...p.months.map((s) => Number((s / 3600).toFixed(2))),
      Number((p.total / 3600).toFixed(2)),
    ]);

  return (
    <PageContainer>
      <PageHeader title="Year" description="Every month of the year, person by person." />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" aria-label="Previous year" title="Previous year (← or j)" onClick={() => setYear(shown - 1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="ts-num min-w-[5rem] text-center text-sm font-semibold">{label}</span>
        <Button variant="outline" size="icon" aria-label="Next year" title="Next year (→ or k)" onClick={() => setYear(shown + 1)}>
          <ChevronRight className="size-4" />
        </Button>
        {/* One grid, two questions. Hours is what was worked; balance is that
            against each person's contract, which is the overtime figure. */}
        <div className="ml-2 flex rounded-md border p-0.5">
          {(['hours', 'balance', 'projects'] as const).map((m) => (
            <Button
              key={m}
              hint={m === 'hours' ? 'Hours worked' : m === 'balance' ? 'Hours against each contract — the overtime balance' : 'Hours by project'}
              size="sm"
              variant={mode === m ? 'default' : 'ghost'}
              className="h-7 px-3 text-xs"
              onClick={() => setMode(m)}
            >
              {m === 'hours' ? 'Hours' : m === 'balance' ? 'Balance' : 'Projects'}
            </Button>
          ))}
        </div>

        {mode !== 'projects' && data && data.people.length > 0 && (
          <ExportMenu
            onCsv={() => downloadCsv(`year-${label}.csv`, [columns.map((c) => c.header), ...rows()])}
            onXlsx={() => downloadXlsx(`year-${label}.xlsx`, label, columns, rows())}
          />
        )}
      </div>

      {isPending && <Skeleton className="h-96 w-full" />}

      {mode === 'balance' && overtime && (
        <Card>
          <CardContent className="p-0">
            <p className="border-b px-4 py-2 text-xs text-muted-foreground">
              Worked against each person&rsquo;s contracted hours, counted to{' '}
              {overtime.asOf} &mdash; days that have not happened are not owed. Holidays
              and approved leave count as time worked, so they are not a shortfall.
            </p>
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-card">Person</TableHead>
                    {headers.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overtime.people.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="sticky left-0 z-10 whitespace-nowrap bg-card font-medium">
                        {p.name}
                        {p.left && <span className="ml-2 text-xs font-normal text-muted-foreground">left</span>}
                        {!p.hasContract && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            no contract
                          </span>
                        )}
                      </TableCell>
                      {p.balance.map((seconds, i) => (
                        <TableCell key={i} className="ts-num text-right text-sm">
                          {/* Only where a target existed. A month with no contract
                              is blank, not a balance of zero - those read
                              differently, and zero would mean "exactly on target". */}
                          {(p.expected[i] ?? 0) === 0 && seconds === 0 ? (
                            <span className="opacity-25">&mdash;</span>
                          ) : (
                            <Signed seconds={seconds} />
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        <Signed seconds={p.totalBalance} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {overtime.people.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="py-10 text-center text-muted-foreground">
                        Nothing to compare in {label}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'projects' && projectYear && (
        <Card>
          <CardContent className="p-0">
            <p className="border-b px-4 py-2 text-xs text-muted-foreground">
              Where each project&rsquo;s hours landed, month by month. The other axis of the
              same year &mdash; the people grid answers who, this one answers what.
            </p>
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-card">Project</TableHead>
                    {headers.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectYear.projects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="sticky left-0 z-10 whitespace-nowrap bg-card font-medium">
                        {p.name}
                        {p.customer && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {p.customer}
                          </span>
                        )}
                        {p.hidden && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">hidden</span>
                        )}
                      </TableCell>
                      {p.months.map((seconds, i) => (
                        <TableCell key={i} className="ts-num text-right text-sm">
                          {seconds > 0 ? formatDuration(seconds) : <span className="opacity-25">—</span>}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {formatDuration(p.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {projectYear.projects.length > 0 && (
                    <TableRow className="bg-muted/40">
                      <TableCell className="sticky left-0 z-10 bg-muted/40 font-semibold">Total</TableCell>
                      {projectYear.monthTotals.map((seconds, i) => (
                        <TableCell key={i} className="ts-num text-right text-sm font-semibold">
                          {seconds > 0 ? formatDuration(seconds) : '—'}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {formatDuration(projectYear.monthTotals.reduce((s, n) => s + n, 0))}
                      </TableCell>
                    </TableRow>
                  )}
                  {projectYear.projects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="py-10 text-center text-muted-foreground">
                        Nothing booked in {label}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'hours' && data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-card">Person</TableHead>
                    {headers.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.people.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="sticky left-0 z-10 whitespace-nowrap bg-card font-medium">
                        {p.name}
                        {p.left && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">left</span>
                        )}
                      </TableCell>
                      {p.months.map((seconds, i) => (
                        <TableCell key={i} className="ts-num text-right text-sm">
                          {/* A dash, not 0:00 - a month somebody did not work should
                              not read like a month they worked nothing. */}
                          {seconds > 0 ? formatDuration(seconds) : <span className="opacity-25">—</span>}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {formatDuration(p.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.people.length > 0 && (
                    <TableRow className="bg-muted/40">
                      <TableCell className="sticky left-0 z-10 bg-muted/40 font-semibold">Total</TableCell>
                      {data.monthTotals.map((seconds, i) => (
                        <TableCell key={i} className="ts-num text-right text-sm font-semibold">
                          {seconds > 0 ? formatDuration(seconds) : '—'}
                        </TableCell>
                      ))}
                      <TableCell className="ts-num text-right font-semibold">
                        {formatDuration(data.monthTotals.reduce((s, n) => s + n, 0))}
                      </TableCell>
                    </TableRow>
                  )}
                  {data.people.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="py-10 text-center text-muted-foreground">
                        Nothing recorded in {label}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------ inactive projects -- */}
      {idle && (
        <Card>
          <CardContent className="space-y-3 py-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">
                  Nobody has booked to these in {idleDays} days
                </h3>
                <p className="text-xs text-muted-foreground">
                  Still offered in every picker. Retiring one keeps its history and stops
                  new bookings.
                </p>
              </div>
              <div className="flex gap-1">
                {[30, 90, 180, 365].map((d) => (
                  <Button
                    key={d}
                    hint={`Projects with no bookings in the last ${d} days`}
                    size="sm"
                    variant={d === idleDays ? 'default' : 'outline'}
                    onClick={() => setIdleDays(d)}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
            </div>

            {idle.projects.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Every visible project has been used in the last {idleDays} days.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {idle.projects.map((p) => (
                  <span
                    key={p.id}
                    className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs"
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">{p.customer}</span>
                    <Badge variant="outline" className="font-normal">
                      {p.lastBooked ? `last ${p.lastBooked}` : 'never used'}
                    </Badge>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

/** A signed duration, so ahead and behind never look alike. */
function Signed({ seconds }: { seconds: number }) {
  if (seconds === 0) return <span className="opacity-40">0:00</span>;
  const behind = seconds < 0;
  return (
    <span className={behind ? 'text-destructive' : 'text-primary'}>
      {behind ? '−' : '+'}
      {formatDuration(Math.abs(seconds))}
    </span>
  );
}
